// src/scene/lighting.ts
import * as THREE from 'three';
import { Reflector } from 'three/addons/objects/Reflector.js';

const COOL_BG = 0x070709;
const WARM_BG = 0x0c0806;

// Polished-metal reflection: a blurred, depth-scaled tap of the mirror render
// target instead of a 1:1 mirror sample. Blur grows with view distance (near
// the contact line it stays crisp, farther away it smears) and is biased
// vertically (uAniso.y) the way a buffed floor streaks light — that gradient
// is what separates "polished metal" from a flat sheet of "glass".
const POLISHED_SHADER = {
  uniforms: {
    color: { value: null as THREE.Color | null },
    tDiffuse: { value: null as THREE.Texture | null },
    textureMatrix: { value: new THREE.Matrix4() },
    uBlur: { value: 0.006 },
    uAniso: { value: new THREE.Vector2(1.0, 1.55) },
  },
  vertexShader: /* glsl */ `
    uniform mat4 textureMatrix;
    varying vec4 vUv;
    varying float vDepth;
    #include <common>
    #include <logdepthbuf_pars_vertex>
    void main() {
      vUv = textureMatrix * vec4( position, 1.0 );
      vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );
      vDepth = -mvPosition.z;
      gl_Position = projectionMatrix * mvPosition;
      #include <logdepthbuf_vertex>
    }`,
  fragmentShader: /* glsl */ `
    uniform vec3 color;
    uniform sampler2D tDiffuse;
    uniform float uBlur;
    uniform vec2 uAniso;
    varying vec4 vUv;
    varying float vDepth;
    #include <logdepthbuf_pars_fragment>
    float blendOverlay( float base, float blend ) {
      return( base < 0.5 ? ( 2.0 * base * blend ) : ( 1.0 - 2.0 * ( 1.0 - base ) * ( 1.0 - blend ) ) );
    }
    vec3 blendOverlay( vec3 base, vec3 blend ) {
      return vec3( blendOverlay( base.r, blend.r ), blendOverlay( base.g, blend.g ), blendOverlay( base.b, blend.b ) );
    }
    void main() {
      #include <logdepthbuf_fragment>
      vec2 uv = vUv.xy / vUv.w;
      // radius grows with distance from the camera (contact line stays sharp)
      vec2 r = uBlur * ( 0.4 + vDepth * 0.06 ) * uAniso;
      // 9-tap cross+diagonal box blur (unrolled — no GLSL ES 1.0 arrays)
      vec3 sum = texture2D( tDiffuse, uv ).rgb * 2.0;
      sum += texture2D( tDiffuse, uv + vec2(  r.x, 0.0 ) ).rgb;
      sum += texture2D( tDiffuse, uv + vec2( -r.x, 0.0 ) ).rgb;
      sum += texture2D( tDiffuse, uv + vec2( 0.0,  r.y ) ).rgb;
      sum += texture2D( tDiffuse, uv + vec2( 0.0, -r.y ) ).rgb;
      sum += texture2D( tDiffuse, uv + vec2(  r.x,  r.y ) * 0.7 ).rgb;
      sum += texture2D( tDiffuse, uv + vec2( -r.x,  r.y ) * 0.7 ).rgb;
      sum += texture2D( tDiffuse, uv + vec2(  r.x, -r.y ) * 0.7 ).rgb;
      sum += texture2D( tDiffuse, uv + vec2( -r.x, -r.y ) * 0.7 ).rgb;
      vec3 base = sum / 10.0;
      gl_FragColor = vec4( blendOverlay( base, color ), 1.0 );
      #if defined( TONE_MAPPING )
        gl_FragColor.rgb = toneMapping( gl_FragColor.rgb );
      #endif
      #include <colorspace_fragment>
    }`,
};

export class Lighting {
  constructor(private scene: THREE.Scene, wallWidth: number, depth = 12) {
    scene.background = new THREE.Color(COOL_BG);
    // Exp fog swallows the wall's far top and the room edges into black.
    scene.fog = new THREE.FogExp2(COOL_BG, 0.045);

    // Faint floor reflection at the foot of the wall. Half-res for perf.
    try {
      const geo = new THREE.PlaneGeometry(depth, wallWidth + 4);
      const floor = new Reflector(geo, {
        clipBias: 0.003,
        // lower res than glass would use — the bilinear upscale adds to the
        // shader blur, reading as roughness rather than a sharp mirror
        textureWidth: Math.floor(window.innerWidth * window.devicePixelRatio * 0.4),
        textureHeight: Math.floor(window.innerHeight * window.devicePixelRatio * 0.4),
        color: 0x0c0c0e, // very faint reflection tint (was 0x222226 — read as too strong)
        shader: POLISHED_SHADER,
      });
      floor.rotation.x = -Math.PI / 2;                  // lie flat
      floor.position.set(depth / 2 - 4, 0, wallWidth / 2); // in front of the wall, centred across
      scene.add(floor);
    } catch {
      // Reflector unsupported (headless/old GL) — skip; the dark fog floor still reads as a room.
    }
  }

  /** Warmer, denser atmosphere for the QR/exhibition entry. */
  setWystawaMood(): void {
    (this.scene.background as THREE.Color).setHex(WARM_BG);
    (this.scene.fog as THREE.FogExp2).color.setHex(WARM_BG);
    (this.scene.fog as THREE.FogExp2).density = 0.05;
  }
}
